import React, { useMemo } from "react";
import { Alert, Badge, Card, Col, Row, Table } from "react-bootstrap";
import katex from "katex";

function prettyJson(v) {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function displayValue(v) {
  if (v === null || typeof v === "undefined") return "—";
  if (typeof v === "string") return v.length ? v : "—";
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return prettyJson(v);
}

function formatChemicalFormulaToLatex(input) {
  const raw = typeof input === "string" ? input.trim() : "";
  if (!raw) return "";

  let s = raw;

  // Normalize dot separators to \cdot
  s = s.replace(/\s*[·•∙]\s*/g, " \\cdot ");

  // Charges like Co2+, Na+, SO4- (simple)
  s = s.replace(/([A-Z][a-z]?)(\d+)([+-]+)/g, (_m, el, n, sign) => `${el}^{${n}${sign}}`);
  s = s.replace(/([A-Z][a-z]?)([+-]+)/g, (_m, el, sign) => `${el}^{${sign}}`);

  // Subscripts after element symbols and after closing parens/brackets
  s = s.replace(/([A-Za-z)\]])(\d+)/g, (_m, a, n) => `${a}_{${n}}`);

  // Keep commas readable in math mode
  s = s.replace(/,\s*/g, ",\\,");

  // Preserve visible spacing a bit (math mode collapses spaces)
  s = s.replace(/\s+/g, "\\,");

  return s;
}

function LatexInline({ latex, fallback }) {
  const html = useMemo(() => {
    if (!latex) return null;
    try {
      return katex.renderToString(latex, { displayMode: false, throwOnError: false, strict: "ignore" });
    } catch {
      return null;
    }
  }, [latex]);

  if (!html) return fallback ?? "—";
  return <span className="text-break" dangerouslySetInnerHTML={{ __html: html }} />;
}

function KeyValueTable({ rows }) {
  return (
    <div className="table-responsive">
      <Table hover className="table-vcenter table-songsnap" size="sm">
        <tbody>
          {(rows || [])
            .filter((r) => r && r.key)
            .map((r) => (
              <tr key={r.key}>
                <td className="text-muted small" style={{ width: 220 }}>
                  {r.key}
                </td>
                <td style={{ wordBreak: "break-word" }}>{r.value}</td>
              </tr>
            ))}
        </tbody>
      </Table>
    </div>
  );
}

function ImageGrid({ images }) {
  const items = (images || []).filter(Boolean);
  if (!items.length) return <div className="text-muted small">—</div>;
  return (
    <Row className="g-2">
      {items.map((src) => (
        <Col key={src} xs={6} sm={4} md={3} lg={2}>
          <img
            alt=""
            src={src}
            title={src}
            style={{
              width: "100%",
              aspectRatio: "1 / 1",
              objectFit: "cover",
              borderRadius: 8
            }}
          />
        </Col>
      ))}
    </Row>
  );
}

/** Icon-style images in content sections (qualities, zodiac, chakras, elements, …) */
const CONTENT_ITEM_IMAGE_THUMB_MAX = 96;
const CONTENT_ITEM_ICON_BOX_BG = "#1a1a1a";

function ContentItemIconImage({ src }) {
  if (!src) return null;
  const size = CONTENT_ITEM_IMAGE_THUMB_MAX;
  return (
    <div
      className="mt-2"
      style={{
        width: size,
        height: size,
        borderRadius: 10,
        backgroundColor: CONTENT_ITEM_ICON_BOX_BG,
        border: "1px solid #dee2e6",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        flexShrink: 0
      }}
    >
      <img
        alt=""
        src={src}
        title={src}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block"
        }}
      />
    </div>
  );
}

function TagList({ tags }) {
  const items = (tags || []).filter(Boolean);
  if (!items.length) return <div className="text-muted small">—</div>;
  return (
    <div className="d-flex flex-column gap-2">
      {items.map((t, idx) => (
        <Card key={`${t.tag_name || "tag"}-${t.key || idx}`} className="mb-0">
          <Card.Body className="py-2">
            <div className="d-flex align-items-start justify-content-between gap-2">
              <div className="fw-bold">{displayValue(t.key)}</div>
              {t.tag_name ? (
                <Badge bg="secondary" className="badge-pill">
                  {t.tag_name}
                </Badge>
              ) : null}
            </div>
            <div className="text-muted small mt-1" style={{ whiteSpace: "pre-wrap" }}>
              {displayValue(t.value)}
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

function ContentSection({ section }) {
  if (!section) return null;
  const title = section.title || section.section_id || "Section";
  const description = section.description;
  const range = section.range;
  const img = section.img;

  const images = section.images;
  const bullets = section.bullets;
  const attributes = section.attributes;
  const details = section.details;
  const faq = section.faq;
  const items = section.items;
  const metrics = section.metrics;

  function renderAttributeValue(key, value) {
    const k = (key || "").toString().trim().toLowerCase();
    const v = typeof value === "string" ? value : null;
    if (k === "formula" && v) {
      const latex = formatChemicalFormulaToLatex(v);
      return <LatexInline latex={latex} fallback={displayValue(value)} />;
    }
    return displayValue(value);
  }

  return (
    <Card>
      <Card.Header>
        <Card.Title className="d-flex align-items-center justify-content-between gap-2">
          <span>{title}</span>
          {section.section_id ? (
            <Badge bg="secondary" className="badge-pill">
              {section.section_id}
            </Badge>
          ) : null}
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {img ? (
          <div className="mb-3">
            <img alt="" src={img} title={img} style={{ maxWidth: "100%", borderRadius: 8 }} />
          </div>
        ) : null}

        {range ? <div className="text-muted small mb-2">Range: {displayValue(range)}</div> : null}

        {description ? (
          <div className="mb-3" style={{ whiteSpace: "pre-wrap" }}>
            {description}
          </div>
        ) : null}

        {Array.isArray(bullets) && bullets.length ? (
          <ul className="mb-3">
            {bullets.filter(Boolean).map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        ) : null}

        {Array.isArray(attributes) && attributes.length ? (
          <KeyValueTable
            rows={attributes.map((a, idx) => ({
              key: a?.key || `attribute_${idx + 1}`,
              value: renderAttributeValue(a?.key, a?.value)
            }))}
          />
        ) : null}

        {Array.isArray(details) && details.length ? (
          <KeyValueTable
            rows={details.map((d, idx) => {
              const k = d?.name || `detail_${idx + 1}`;
              let v;
              if (d?.type === "badge" && Array.isArray(d?.value)) {
                v = d.value.filter(Boolean).length ? (
                  <div className="d-flex flex-wrap gap-1">
                    {d.value.filter(Boolean).map((b, i) => (
                      <Badge key={`${b}-${i}`} bg="secondary" className="badge-pill">
                        {b}
                      </Badge>
                    ))}
                  </div>
                ) : "—";
              } else {
                v = renderAttributeValue(k, d?.value);
              }
              return { key: k, value: v };
            })}
          />
        ) : null}

        {Array.isArray(faq) && faq.length ? (
          <div className="d-flex flex-column gap-2">
            {faq.map((f, idx) => (
              <Card key={f?.question || idx} className="mb-0">
                <Card.Body className="py-2">
                  <div className="fw-bold">{displayValue(f?.question)}</div>
                  <div className="text-muted small mt-1" style={{ whiteSpace: "pre-wrap" }}>
                    {displayValue(f?.answer)}
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        ) : null}

        {Array.isArray(metrics) && metrics.length ? (
          <div className="table-responsive">
            <Table hover className="table-vcenter table-songsnap" size="sm">
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Metric</th>
                  <th style={{ width: 160 }}>Selected</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {metrics.map((m, idx) => (
                  <tr key={m?.name || idx}>
                    <td className="text-muted small">{displayValue(m?.name)}</td>
                    <td>{displayValue(m?.selected)}</td>
                    <td style={{ whiteSpace: "pre-wrap" }}>
                      {m?.levels?.length ? (
                        <div className="small mb-1">
                          Levels: {(m.levels || []).filter(Boolean).join(" · ")}
                        </div>
                      ) : null}
                      <div className="text-muted small">{displayValue(m?.description)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}

        {Array.isArray(items) && items.length ? (
          <Row className="g-2">
            {items.map((it, idx) => {
              const imgUrl = it?.image;
              const itTitle = it?.title;
              const itDesc = it?.description;
              const key = `${itTitle || "item"}-${imgUrl || idx}`;
              return (
                <Col key={key} xs={12} sm={6} md={4} lg={3}>
                  <Card className="mb-0 h-100">
                    <Card.Body className="py-2">
                      <div className="fw-bold">{displayValue(itTitle)}</div>
                      {imgUrl ? <ContentItemIconImage src={imgUrl} /> : null}
                      {itDesc ? (
                        <div className="text-muted small mt-2" style={{ whiteSpace: "pre-wrap" }}>
                          {itDesc}
                        </div>
                      ) : null}
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : null}

        {Array.isArray(images) && images.length ? (
          <div className="mt-3">
            <ImageGrid images={images} />
          </div>
        ) : null}

        {!description &&
        !range &&
        !img &&
        !Array.isArray(bullets) &&
        !Array.isArray(attributes) &&
        !Array.isArray(details) &&
        !Array.isArray(faq) &&
        !Array.isArray(metrics) &&
        !Array.isArray(items) &&
        !Array.isArray(images) ? (
          <pre className="mb-0 small" style={{ whiteSpace: "pre-wrap" }}>
            {prettyJson(section)}
          </pre>
        ) : null}
      </Card.Body>
    </Card>
  );
}

export function isGemLikeRockData(data) {
  if (!data || typeof data !== "object") return false;
  if (data.result && typeof data.result === "object" && !data.uid && !data.preferred_name) {
    return isGemLikeRockData(data.result);
  }
  return Boolean(
    data.content?.length ||
      data.latin_name ||
      data.preferred_name ||
      data.common_name ||
      data.tags?.length ||
      data.images?.length ||
      data.main_image_url
  );
}

export default function RockGemLikeDetail({ gem, showCrawlFields = false, language_code }) {
  const rowsGeneral = useMemo(() => {
    if (!gem) return [];
    const langRows = [];
    if (language_code) {
      langRows.push({ key: "language_code", value: displayValue(language_code) });
    }
    if (gem.requested_language) {
      langRows.push({ key: "requested_language", value: displayValue(gem.requested_language) });
    }
    if (gem.resolved_language) {
      langRows.push({ key: "resolved_language", value: displayValue(gem.resolved_language) });
    }
    return [
      ...langRows,
      { key: "id", value: displayValue(gem.id) },
      { key: "uid", value: displayValue(gem.uid) },
      { key: "category_cms_uid", value: displayValue(gem.category_cms_uid) },
      { key: "latin_name", value: displayValue(gem.latin_name) },
      { key: "preferred_name", value: displayValue(gem.preferred_name) },
      { key: "common_name", value: displayValue(gem.common_name) },
      {
        key: "common_names",
        value: Array.isArray(gem.common_names) ? (
          gem.common_names.filter(Boolean).length ? (
            <div className="d-flex flex-wrap gap-1">
              {gem.common_names.filter(Boolean).map((n) => (
                <Badge key={n} bg="secondary" className="badge-pill">
                  {n}
                </Badge>
              ))}
            </div>
          ) : (
            "—"
          )
        ) : (
          displayValue(gem.common_names)
        )
      },
      { key: "rock_type", value: displayValue(gem.rock_type) },
      { key: "rank", value: displayValue(gem.rank) },
      { key: "source", value: displayValue(gem.source) },
      { key: "created_at", value: displayValue(gem.created_at) },
      { key: "updated_at", value: displayValue(gem.updated_at) }
    ];
  }, [gem, language_code]);

  const rowsCrawl = useMemo(() => {
    if (!gem) return [];
    return [
      { key: "cms_crawled_at", value: displayValue(gem.cms_crawled_at) },
      { key: "cms_crawl_status", value: displayValue(gem.cms_crawl_status) },
      { key: "cms_crawl_error", value: displayValue(gem.cms_crawl_error) }
    ];
  }, [gem]);

  const rowsStaticUrl = useMemo(() => {
    if (!gem?.static_url) return [];
    const fmt = (u) =>
      u ? (
        <span className="small text-break" style={{ wordBreak: "break-word" }}>
          {u}
        </span>
      ) : (
        "—"
      );
    return [
      { key: "light", value: fmt(gem.static_url.light) },
      { key: "dark", value: fmt(gem.static_url.dark) }
    ];
  }, [gem]);

  /** Only these keys from API `attributes` object (rest hidden). */
  const rowsAttributesFromRaw = useMemo(() => {
    const a = gem?.attributes;
    if (!a || typeof a !== "object") {
      return [
        { key: "latin_name", value: "—" },
        { key: "main_image_url", value: "—" },
        { key: "rank", value: "—" }
      ];
    }
    const url = a.main_image_url;
    return [
      { key: "latin_name", value: displayValue(a.latin_name) },
      {
        key: "main_image_url",
        value: url ? (
          <div>
            <div className="small text-muted text-break mb-2" style={{ wordBreak: "break-word" }}>
              {url}
            </div>
            <div>
              <img
                alt=""
                src={url}
                title={url}
                style={{ maxWidth: "100%", maxHeight: 220, borderRadius: 8, objectFit: "contain" }}
              />
            </div>
          </div>
        ) : (
          "—"
        )
      },
      { key: "rank", value: displayValue(a.rank) }
    ];
  }, [gem]);

  if (!gem) return null;

  return (
    <>
      {gem.language_fallback ? (
        <Alert variant="warning" className="mb-3">
          No row in <code>gems_{gem.requested_language || language_code}</code> — showing English fallback (
          <code>gems</code>).
        </Alert>
      ) : null}
    <Row className="row-deck row-cards g-3">
          <Col xs={12} lg={6}>
            <Card>
              <Card.Header>
                <Card.Title>General</Card.Title>
              </Card.Header>
              <Card.Body>
                <KeyValueTable rows={rowsGeneral} />
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={6}>
            <Card>
              <Card.Header>
                <Card.Title>Main image</Card.Title>
              </Card.Header>
              <Card.Body>
                {gem.main_image_url ? (
                  <img
                    alt=""
                    src={gem.main_image_url}
                    title={gem.main_image_url}
                    style={{ maxWidth: "100%", borderRadius: 10 }}
                  />
                ) : (
                  <div className="text-muted small">—</div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={6}>
            <Card>
              <Card.Header>
                <Card.Title>Tags</Card.Title>
              </Card.Header>
              <Card.Body>
                <TagList tags={gem.tags} />
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} lg={6}>
            <Card>
              <Card.Header>
                <Card.Title>Images</Card.Title>
              </Card.Header>
              <Card.Body>
                <ImageGrid images={gem.images} />
              </Card.Body>
            </Card>
          </Col>

          {showCrawlFields && rowsStaticUrl.length ? (
            <Col xs={12} lg={6}>
              <Card>
                <Card.Header>
                  <Card.Title>Static URL</Card.Title>
                </Card.Header>
                <Card.Body>
                  <KeyValueTable rows={rowsStaticUrl} />
                </Card.Body>
              </Card>
            </Col>
          ) : null}

          {showCrawlFields ? (
            <Col xs={12} lg={6}>
              <Card>
                <Card.Header>
                  <Card.Title>Crawl</Card.Title>
                </Card.Header>
                <Card.Body>
                  <KeyValueTable rows={rowsCrawl} />
                </Card.Body>
              </Card>
            </Col>
          ) : null}

          <Col xs={12}>
            <Card>
              <Card.Header>
                <Card.Title className="h5 mb-0">Attributes</Card.Title>
              </Card.Header>
              <Card.Body>
                <KeyValueTable rows={rowsAttributesFromRaw} />
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12}>
            <Card>
              <Card.Header>
                <Card.Title>Content sections</Card.Title>
              </Card.Header>
              <Card.Body>
                <Row className="g-3">
                  {(gem.content || []).map((s, idx) => (
                    <Col key={s?.section_id || idx} xs={12}>
                      <ContentSection section={s} />
                    </Col>
                  ))}
                  {!gem.content?.length ? (
                    <Col xs={12}>
                      <div className="text-muted small">—</div>
                    </Col>
                  ) : null}
                </Row>
              </Card.Body>
            </Card>
          </Col>
    </Row>
    </>
  );
}

